import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const EmployeehrCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('auth-token');
  const searchParams = new URLSearchParams(location.search);
  const locationEmployeeId = searchParams.get('employeeId') || null;
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [centers, setCenters] = useState({});
  const [loading, setLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [centersFetched, setCentersFetched] = useState(false);
  const baselineRef = useRef(null);

  const allCenterIds = useMemo(() => Object.keys(centers), [centers]);

  const normalizeDepartments = (depts = []) =>
    depts.map((dept) => {
      const rolesArr = Array.isArray(dept.role)
        ? dept.role.map((r) => ({ id: r.id, name: r.role }))
        : [];
      return {
        id: dept.id,
        name: dept.name,
        roles: rolesArr,
        rolesLabel: rolesArr.length ? rolesArr.map((r) => r.name).join(', ') : 'No Roles',
      };
    });

  const fetchCenters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL_FEEDBACK_FETCH, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.data?.success && response.data?.data?.academyNameMap) {
        setCenters(response.data.data.academyNameMap);
        setCentersFetched(true);
      }
    } catch (error) {
      console.error('Error fetching centers:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to fetch centers data',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  // Updated buildSubmitPayload to handle "All" selection
  const buildSubmitPayload = ({
    id = employeeId,
    n = name,
    d = dob,
    g = gender,
    m = mobileNumber,
    c = employeeCode,
    deptId = selectedDeptId,
    roleId = selectedRoleId,
    selectedCenterOptions = selectedCenters,
  } = {}) => {
    // Check if "All" is selected
    const hasAllSelected = selectedCenterOptions.some(opt => opt.value === 'all');
    // If "All" is selected, send all center IDs
    const academyIds = hasAllSelected
      ? allCenterIds
      : selectedCenterOptions
        .map(opt => opt.value)
        .filter(id => id !== 'all');
    console.log("Academy Ids", academyIds)
    return {
      id: id || null,
      name: (n || '').trim(),
      dob: d || '',
      gender: g || '',
      mobileNumber: m ? `+91${m.replace(/^\+91/, '').trim()}` : '',
      employeeCode: (c || '').trim(),
      departmentId: deptId || null,
      roleId: roleId || null,
      academyIds: academyIds
    };
  };

  const departmentOptions = useMemo(
    () => (departments || []).map((d) => ({ value: d.id, label: d.name })),
    [departments]
  );

  const roleOptions = useMemo(() => {
    const dept = (departments || []).find((d) => d.id === selectedDeptId);
    const rolesArr = Array.isArray(dept?.roles) ? dept.roles : [];
    return rolesArr.map((r) => ({ value: r.id, label: r.name }));
  }, [departments, selectedDeptId]);

  // Updated centerOptions with "All" option
  const centerOptions = useMemo(() => {
    const centerList = Object.entries(centers).map(([id, name]) => ({
      value: id,
      label: name,
    }));

    return [
      { value: 'all', label: 'All Centers' },
      ...centerList
    ];
  }, [centers]);

  const validate = () => {
    const dobDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - dobDate.getFullYear();
    if (!selectedDeptId) return 'Please select a Department.';
    if (!selectedRoleId) return 'Please select a Role.';
    if (!name.trim()) return 'Name is required.';
    if (!/^[A-Za-z\s]+$/.test(name.trim())) return 'Name should contain only letters and spaces.';
    if (/\s{2,}/.test(name)) return 'Name cannot contain multiple consecutive spaces.';
    if (!dob) return 'Date of Birth is required.';
    if (dobDate > today) return 'Date of Birth cannot be in the future.';
    if (age > 100) return 'Please enter a valid Date of Birth.';
    if (!gender) return 'Gender is required.';
    if (!mobileNumber.trim()) return 'Mobile Number is required.';
    if (!/^\d{10}$/.test(mobileNumber.trim())) return 'Mobile Number must be exactly 10 digits.';
    if (/^(.)\1{9}$/.test(mobileNumber)) return 'Invalid Mobile Number.';
    if (/^0/.test(mobileNumber)) return 'Mobile Number cannot start with 0.';
    if (!employeeCode.trim()) return 'Employee Code is required.';
    if (selectedCenters.length === 0) return 'Please select at least one center.';
    return null;
  };


  useEffect(() => {
    if (!centersFetched) return;
    const run = async () => {
      try {
        const [deptResp] = await Promise.all([
          axios.get(process.env.REACT_APP_API_DEPARTMENT_LIST, {
            headers: {
              'Content-Type': 'application/json',
              'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
            },
          }),
        ]);

        const deptList = deptResp.data?.data?.departmentsWithRoles || [];
        setDepartments(normalizeDepartments(deptList));

        if (locationEmployeeId) {
          setEmployeeId(locationEmployeeId);
          const url = `${process.env.REACT_APP_API_EMPLOYEE_LIST_HR}?employeeId=${locationEmployeeId}`;
          const res = await axios.get(url,
            {
              headers: {
                'Content-Type': 'application/json',
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
              },
            }
          );

          const data = res.data;
          if (data?.success) {
            const emp = Array.isArray(data?.data?.employees) && data.data.employees.length > 0
              ? data.data.employees[0]
              : {};
            const deptId = emp.departmentId ?? null
            const roleId = emp.roleId ?? null;

            const employeeAcademyIds = emp.academyIds
            const hasAllAcademyIds = employeeAcademyIds.length === Object.keys(centers).length;
            const academySelections = hasAllAcademyIds
              ? [{ value: 'all', label: 'All Centers' }]
              : employeeAcademyIds.map(academyId => ({
                value: academyId,
                label: centers[Number(academyId)] || `Center ${academyId}`
              }));
            setName(emp.name || '');
            setDob((emp.dob || '').slice(0, 10));
            setGender(emp.gender || '');
            setMobileNumber((emp.mobileNumber || '').replace(/^\+91/, ''));
            setEmployeeCode(emp.employeeCode || '');
            setSelectedDeptId(deptId);
            setSelectedRoleId(roleId);
            setSelectedCenters(academySelections);

            baselineRef.current = buildSubmitPayload({
              id: emp.id || locationEmployeeId,
              n: emp.name || '',
              d: (emp.dob || '').slice(0, 10),
              g: emp.gender || '',
              m: emp.mobileNumber || emp.mobile_number || '',
              c: emp.employeeCode || emp.employee_code || '',
              deptId,
              roleId,
              centers: academySelections,
            });
          } else {
            throw new Error('Failed to fetch employee details');
          }
        } else {
          baselineRef.current = buildSubmitPayload({
            id: null, n: '', d: '', g: '', m: '', c: '', deptId: null, roleId: null, centers: []
          });
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: err?.response?.data?.message || err.message || 'Failed to fetch data',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [centersFetched]);

  useEffect(() => {
    fetchCenters();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  const handleDepartmentChange = (opt) => {
    const newDeptId = opt ? opt.value : null;
    setSelectedDeptId(newDeptId);
    setSelectedRoleId(null);
  };

  const handleRoleChange = (opt) => setSelectedRoleId(opt ? opt.value : null);

  const handleCentersChange = (selectedOptions) => {
    const options = selectedOptions || [];

    const allSelected = options.some(option => option.value === 'all');

    if (allSelected) {
      setSelectedCenters([{ value: 'all', label: 'All Centers' }]);
    } else if (options.length > 0) {
      const filteredOptions = options.filter(option => option.value !== 'all');
      setSelectedCenters(filteredOptions);
    } else {
      setSelectedCenters([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const newPayload = buildSubmitPayload({});
    const oldPayload = baselineRef.current || {};

    if (employeeId) {
      const { digest, html } = buildDiffPreview(oldPayload, newPayload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(
        process.env.REACT_APP_API_EMPLOYEE_HR_CREATE,
        newPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        }
      );

      console.log(resp);
      const data = resp.data;
      if (locationEmployeeId) {
        if (!(resp.status === 200 && data?.success)) {
          throw new Error(data?.success_message || 'Something went wrong!');
        }
      } else {
        if (!(resp.status === 201 && data?.success)) {
          throw new Error(data?.success_message || 'Something went wrong!');
        }
      }

      fireAudit({
        entity: 'employee',
        affected_id: employeeId || null,
        action: employeeId ? 'update' : 'create',
        businessApi: process.env.REACT_APP_EMPLOYEE_API_CREATE || process.env.REACT_APP_API_CREATE,
        businessMethod: 'POST',
        oldPayload,
        newPayload,
        section: 'HR',
        subSection: employeeId ? 'Employee Update' : 'Employee Creation',
        extra: { page: 'EmployeehrCreation' },
      });

      Swal.fire({
        icon: 'success',
        title: employeeId ? 'Employee Updated!' : 'Employee Created!',
        text: data?.success_message || 'Success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });

      baselineRef.current = newPayload;

      if (employeeId) {
        navigate(-1);
      } else {
        setName('');
        setDob('');
        setGender('');
        setMobileNumber('');
        setEmployeeCode('');
        setSelectedDeptId(null);
        setSelectedRoleId(null);
        setSelectedCenters([]);
        navigate(-1);
      }
    } catch (err) {
      console.log(err);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err?.response?.data?.data?.error || err?.response?.data?.message || err?.message || 'Failed to save employee.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/employeeHr" className="breadcrumb-link">
          Employees
        </Link>{' '}
        &gt; {employeeId ? 'Update Employee' : 'Employee Creation'}
      </div>

      <form onSubmit={handleSubmit} noValidate>
        {/* Department */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="department"
            isMulti={false}
            options={departmentOptions}
            onChange={handleDepartmentChange}
            value={departmentOptions.find((o) => o.value === selectedDeptId) || null}
            className={`walkin-form-dropdown ${selectedDeptId ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Department"
            isClearable
            isDisabled={loading}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menu: (p) => ({ ...p, zIndex: 1001 }),
            }}
          />
          {selectedDeptId && <label htmlFor="department">Department</label>}
        </div>

        {/* Role (depends on Department) */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="role"
            isMulti={false}
            isDisabled={!selectedDeptId || loading}
            options={roleOptions}
            onChange={handleRoleChange}
            value={roleOptions.find((o) => o.value === selectedRoleId) || null}
            className={`walkin-form-dropdown ${selectedRoleId ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder={selectedDeptId ? 'Select Role' : 'Select a department first'}
            isClearable
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menu: (p) => ({ ...p, zIndex: 1001 }),
            }}
          />
          {selectedRoleId && <label htmlFor="role">Role</label>}
        </div>

        {/* Centers Multi-Select with All option */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="centers"
            isMulti
            options={centerOptions}
            onChange={handleCentersChange}
            value={selectedCenters}
            className={`walkin-form-dropdown ${selectedCenters.length > 0 ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Centers"
            isClearable
            isDisabled={loading}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menu: (p) => ({ ...p, zIndex: 1001 }),
            }}
            closeMenuOnSelect={false}
          />
          {selectedCenters.length > 0 && <label htmlFor="centers">Centers</label>}
        </div>

        {/* Rest of the form remains the same */}
        {/* Name */}
        <div className="floating-select-form">
          <input
            id="employee-name"
            name="name"
            className={`walkin-form ${name ? 'has-value' : ''}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Employee Name"
            required
            disabled={loading}
          />
          {name && <label htmlFor="employee-name">Employee Name</label>}
        </div>

        {/* DOB */}
        <div className="floating-select-form">
          <input
            id="employee-dob"
            name="dob"
            className={`walkin-form ${dob ? 'has-value' : ''}`}
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            placeholder="Date of Birth"
            required
            disabled={loading}
            onClick={(e) => e.target.showPicker()}
          />
          {dob && <label htmlFor="employee-dob">Date of Birth</label>}
        </div>

        {/* Gender */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="gender"
            isMulti={false}
            options={[
              { value: 'MALE', label: 'Male' },
              { value: 'FEMALE', label: 'Female' },
              { value: 'OTHER', label: 'Other' },
            ]}
            onChange={(opt) => setGender(opt ? opt.value : '')}
            value={gender ? { value: gender, label: gender.charAt(0) + gender.slice(1).toLowerCase() } : null}
            className={`walkin-form-dropdown ${gender ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select Gender"
            isClearable
            isDisabled={loading}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
              menu: (p) => ({ ...p, zIndex: 1001 }),
            }}
          />
          {gender && <label htmlFor="gender">Gender</label>}
        </div>

        {/* Mobile Number */}
        <div className="floating-select-form">
          <input
            id="employee-mobile"
            name="mobileNumber"
            className={`walkin-form ${mobileNumber ? 'has-value' : ''}`}
            type="tel"
            value={mobileNumber}
            onChange={(e) => {
              const v = e.target.value;
              setMobileNumber(v);
            }}
            placeholder="Mobile Number"
            inputMode="numeric"
            pattern="\d{10}"
            required
            disabled={loading}
          />
          {mobileNumber && <label htmlFor="employee-mobile">Mobile Number</label>}
        </div>

        {/* Employee Code */}
        <div className="floating-select-form">
          <input
            id="employee-code"
            name="employeeCode"
            className={`walkin-form ${employeeCode ? 'has-value' : ''}`}
            type="text"
            value={employeeCode}
            onChange={(e) => setEmployeeCode(e.target.value)}
            placeholder="Employee Code"
            required
            disabled={loading}
          />
          {employeeCode && <label htmlFor="employee-code">Employee Code</label>}
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={loading}
        >
          {loading ? (employeeId ? 'Updating…' : 'Creating…') : (employeeId ? 'Update Employee' : 'Create Employee')}
        </button>
      </form>
    </div>
  );
};

export default EmployeehrCreation;